import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../Config/api';
import isPasswordValid from '../Utils/isPasswordValid';
import { Icon } from '../Components/UI/Icon';
import PasswordRequirements from '../Components/PasswordRequirements';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../Components/LanguageSelector';

export default function SetFirstPassword() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const ref = searchParams.get('ref');

    const apiBaseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    const defaultLogoUrl = `${apiBaseUrl}/api/auth/media/branding/a-sight_LOGO_DEF.png`;
    const [brandingLogoUrl, setBrandingLogoUrl] = useState(defaultLogoUrl);

    useEffect(() => {
        const url = ref
            ? `${API_ENDPOINTS.brandingLogo}?ref=${encodeURIComponent(ref)}`
            : API_ENDPOINTS.brandingLogo;
        fetch(url)
            .then(res => res.ok ? res.json() : null)
            .then(data => {
                if (data?.logo_url) setBrandingLogoUrl(data.logo_url);
            })
            .catch(() => {});
    }, [ref]);

    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [showPwd, setShowPwd] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [success, setSuccess] = useState(false);
    const [step, setStep] = useState('password'); // password | mfa-offer
    const { t } = useTranslation();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMsg('');

        if (password !== confirm) {
            setErrorMsg('Le password non coincidono');
            return;
        }

        if (!isPasswordValid(password)) {
            setErrorMsg('La password non soddisfa i requisiti di sicurezza');
            return;
        }

        setLoading(true);

        try {
            const res = await fetch(API_ENDPOINTS.changePasswordByToken, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(
                    {
                        token: token,
                        new_password: password
                    }),
            });

            if (res.ok) {
                const data = await res.json();
                // Salva i token JWT ricevuti dal backend per auto-login
                if (data.access) {
                    sessionStorage.setItem('access_token', data.access);
                }
                if (data.refresh) {
                    sessionStorage.setItem('refresh_token', data.refresh);
                }
                setSuccess(true);
                setStep('mfa-offer');
            } else {
                const data = await res.json();
                setErrorMsg(data.detail || 'Errore generico');
            }
        } catch (e) {
            setErrorMsg('Errore di rete');
        } finally {
            setLoading(false);
        }
    };

    const handleSetupMfa = () => {
        // I token JWT sono già stati salvati in sessionStorage durante handleSubmit
        navigate('/mfa-setup');
    };

    return (
        <div className="min-h-screen  flex flex-col gap-3 items-center justify-center bg-gray-50 px-4">
            <div className="w-full max-w-xl flex items-center justify-end mb-2">
                <LanguageSelector />
            </div>

            {step === 'mfa-offer' ? (
              <div className="bg-white p-8 rounded shadow-md space-y-6 w-full max-w-md text-center">
                <img
                    src={brandingLogoUrl}
                    alt="Logo"
                    draggable="false"
                    style={{ maxWidth: 120, margin: '0 auto', display: 'block', userSelect: 'none' }}
                    className="mb-2"
                />
                <div className="flex items-center justify-center gap-2">
                  <Icon name="starshield" className="text-green-600 text-xl" />
                  <h2 className="text-xl font-bold text-green-600">{t('passwordPage.success', 'Password impostata!')}</h2>
                </div>
                <p className="text-gray-600">
                  {t('mfa.offerDescription', 'Vuoi proteggere il tuo account con l\'autenticazione a due fattori (MFA)?')}
                </p>
                <p className="text-sm text-gray-500">
                  {t('mfa.offerHint', 'Potrai usare Google Authenticator o app compatibili per una maggiore sicurezza.')}
                </p>
                {errorMsg && <p className="text-red-600 text-sm">{errorMsg}</p>}
                <div className="space-y-3">
                  <button
                    onClick={handleSetupMfa}
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-white py-2 rounded font-semibold transition"
                  >
                    {t('mfa.setupNow', 'Configura MFA ora')}
                  </button>
                  <button
                    onClick={() => navigate(ref ? `/?ref=${ref}` : '/')}
                    className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 py-2 rounded font-medium transition"
                  >
                    {t('mfa.skipForNow', 'Salta, lo farò dopo')}
                  </button>
                </div>
              </div>
            ) : (
            <form
                onSubmit={handleSubmit}
                className="bg-white p-8 rounded shadow-md space-y-6 relative"
            >
                <img
                    src={brandingLogoUrl}
                    alt="Logo"
                    draggable="false"
                    style={{ maxWidth: 120, margin: '0 auto', display: 'block', userSelect: 'none' }}
                    className="mb-2"
                />
                <h2 className="text-xl font-bold text-gray-800">{t('passwordPage.titleSet')}</h2>



                {/* Griglia a due colonne */}
                <div className="flex gap-14">
                    {/* Colonna sinistra: input */}
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm text-gray-700 mb-1">{t('passwordPage.newLabel')}</label>
                            <div className="relative">
                                <input
                                    type={showPwd ? 'text' : 'password'}
                                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:ring-blue-300 pr-10"
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        if (errorMsg) setErrorMsg('');
                                    }}

                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPwd(prev => !prev)}
                                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                                >
                                    <Icon name={showPwd ? 'eye' : 'eyeslash'} />
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm text-gray-700 mb-1">{t('passwordPage.confirmLabel')}</label>
                            <div className="relative">
                                <input
                                    type={showConfirm ? 'text' : 'password'}
                                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:ring-blue-300 pr-10"
                                    value={confirm}
                                    onChange={(e) => {
                                        setConfirm(e.target.value);
                                        if (errorMsg) setErrorMsg('');
                                    }}

                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirm(prev => !prev)}
                                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                                >
                                    <Icon name={showConfirm ? 'eye' : 'eyeslash'} />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Colonna destra: validazione */}

                    <PasswordRequirements password={password} />
                </div>

                {/* Bottone */}
                <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded disabled:opacity-50"
                >
                    {loading ? t('passwordPage.saving') : t('passwordPage.confirmButton')}
                </button>
                {errorMsg && <p className="text-red-600 absolute -bottom-7 right-0 left-0  text-sm text-center">{errorMsg || t('passwordPage.genericError')}</p>}
                {success && <p className="text-green-600 absolute -bottom-7 right-0 left-0 text-sm text-center">{t('passwordPage.success')}</p>}
            </form>
            )}
        </div>
    );
}
